import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Route,
  FileText,
  Settings,
  Plus,
  LogOut,
  Sliders,
  BookOpen,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const mainNavItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Matkat", url: "/trips", icon: Route },
  { title: "Raportit", url: "/reports", icon: FileText },
  { title: "Hallinta", url: "/manage", icon: Sliders },
  { title: "Ohjeet", url: "/help", icon: BookOpen },
  { title: "Asetukset", url: "/settings", icon: Settings },
];

function AppSidebarContent() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  const displayName = profile?.name || user?.email?.split("@")[0] || "Käyttäjä";
  const displayEmail = user?.email || "";

  const handleLogout = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarHeader className="p-4">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer text-left">
              <Logo dark size="md" className="shrink-0" />
              {!isCollapsed && (
                <div className="flex flex-col">
                  <span className="text-[11px] text-sidebar-foreground/50">Matkalaskut</span>
                </div>
              )}
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Kirjaudu ulos?</AlertDialogTitle>
              <AlertDialogDescription>
                Haluatko varmasti kirjautua ulos palvelusta? Sinut ohjataan etusivulle.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Peruuta</AlertDialogCancel>
              <AlertDialogAction onClick={handleLogout}>Kirjaudu ulos</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SidebarHeader>

      <Separator className="mx-3 w-auto opacity-20" />

      <SidebarContent className="pt-2">
        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/30 text-[10px] uppercase tracking-widest font-semibold">
            Valikko
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavItems.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton asChild tooltip={item.title}>
                    <NavLink
                      to={item.url}
                      end={item.url === "/dashboard"}
                      className="text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-xl"
                      activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-semibold"
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Lisää matka">
                  <NavLink
                    to="/trips/new"
                    className="bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90 font-semibold rounded-xl"
                    activeClassName="bg-sidebar-primary text-sidebar-primary-foreground"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Lisää matka</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-3">
        <Separator className="mb-2 opacity-20" />
        <div className="flex items-center gap-3 rounded-xl p-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary/20 text-sm font-bold text-sidebar-primary">
            {displayName.charAt(0).toUpperCase()}
          </div>
          {!isCollapsed && (
            <div className="flex flex-1 flex-col overflow-hidden">
              <span className="truncate text-sm font-medium text-sidebar-foreground">{displayName}</span>
              <span className="truncate text-[11px] text-sidebar-foreground/40">{displayEmail}</span>
            </div>
          )}
          {!isCollapsed && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 text-sidebar-foreground/40 hover:text-sidebar-foreground hover:bg-sidebar-accent"
              onClick={handleLogout}
              title="Kirjaudu ulos"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}

export default function AppSidebar() {
  return <AppSidebarContent />;
}
